import React, { useState, useMemo, useEffect } from 'react';
import { Table, Input, Button, Space, Tabs, Card, Modal, DatePicker, Form } from 'antd';
import { SearchOutlined, DeleteOutlined, FilterOutlined } from '@ant-design/icons';
import { useFetchAllFacturesAchat } from '../../../hooks/achatFactureHooks';
import dayjs from 'dayjs';

const { TabPane } = Tabs;
const { RangePicker } = DatePicker;

const FactureAchatList = ({ onSelect }) => {
  // State management
  const [fournisseur, setFournisseur] = useState('');
  const [refFacSearch, setRefFacSearch] = useState('');
  const [refBrSearch, setRefBrSearch] = useState('');
  const [dateRange, setDateRange] = useState([null, null]);
  const [form] = Form.useForm();
  const [activeTab, setActiveTab] = useState('notValidated');
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20 });
  const [sorter, setSorter] = useState(() => {
    return {
      field: 'REF_FAC_A',
      order: 'ascend'
    };
  });
  const [hoveredRow, setHoveredRow] = useState(null);

  // Update sort order when tab changes
  useEffect(() => {
    setSorter({
      field: 'REF_FAC_A',
      order: activeTab === 'notValidated' ? 'ascend' : 'descend'
    });
  }, [activeTab]);

  // Combine query parameters for the hook
  const params = useMemo(() => ({
    page: pagination.current,
    pageSize: pagination.pageSize,
    frsSearch: fournisseur || undefined,
    refSearch: refFacSearch || undefined,
    dateFrom: dateRange[0] ? dateRange[0].format('YYYY-MM-DD') : undefined,
    dateTo: dateRange[1] ? dateRange[1].format('YYYY-MM-DD') : undefined,
    sortField: sorter.field,
    sortOrder: sorter.order === 'ascend' ? 'asc' : sorter.order === 'descend' ? 'desc' : undefined,
    status: activeTab,
  }), [pagination.current, pagination.pageSize, fournisseur, refFacSearch, dateRange, sorter.field, sorter.order, activeTab]);

  const { items, total, loading } = useFetchAllFacturesAchat(params);

  const handleDateChange = (dates) => {
    setDateRange(dates || [null, null]);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const clearAllFilters = () => {
    setFournisseur('');
    setRefFacSearch('');
    setRefBrSearch('');
    setDateRange([null, null]);
    form.resetFields();
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // Column definitions
  const columns = [
    {
      title: 'Date Facture',
      dataIndex: 'DATE_FAC',
      key: 'DATE_FAC',
      sorter: true,
      render: (date) => date ? dayjs(date).format('DD/MM/YYYY') : '',
    },
    {
      title: 'Ref. Facture',
      dataIndex: 'REF_FAC_A',
      key: 'REF_FAC_A',
      sorter: true,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Commence par"
            value={selectedKeys[0]}
            onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => {
              confirm();
              setRefFacSearch(selectedKeys[0]);
              setPagination(prev => ({ ...prev, current: 1 }));
            }}
            style={{ marginBottom: 8, display: 'block' }}
            ref={node => node && setTimeout(() => node.focus(), 0)}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => {
                confirm();
                setRefFacSearch(selectedKeys[0]);
                setPagination(prev => ({ ...prev, current: 1 }));
              }}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Rechercher
            </Button>
            <Button
              onClick={() => {
                clearFilters();
                setRefFacSearch('');
                setPagination(prev => ({ ...prev, current: 1 }));
              }}
              size="small"
              style={{ width: 90 }}
            >
              Réinitialiser
            </Button>
          </Space>
        </div>
      ),
      filterIcon: filtered => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
      render: (text, record) => (
        <span
          style={{
            position: 'relative',
            fontWeight: hoveredRow === record.REF_FAC_A ? 'bold' : 'normal',
            transition: 'font-weight 0.3s ease'
          }}
        >
          {text}
          <span
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              width: hoveredRow === record.REF_FAC_A ? '100%' : '0%',
              height: '1px',
              backgroundColor: '#000',
              transition: 'width 0.3s ease'
            }}
          />
        </span>
      )
    },
    {
      title: 'Fournisseur',
      dataIndex: 'FOURNISSEUR',
      key: 'FOURNISSEUR',
      width: 300,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Rechercher Fournisseur"
            value={selectedKeys[0]}
            onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => {
              confirm();
              setFournisseur(selectedKeys[0]);
              setPagination(prev => ({ ...prev, current: 1 }));
            }}
            style={{ marginBottom: 8, display: 'block' }}
            ref={node => node && setTimeout(() => node.focus(), 0)}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => {
                confirm();
                setFournisseur(selectedKeys[0]);
                setPagination(prev => ({ ...prev, current: 1 }));
              }}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Rechercher
            </Button>
            <Button
              onClick={() => {
                clearFilters();
                setFournisseur('');
                setPagination(prev => ({ ...prev, current: 1 }));
              }}
              size="small"
              style={{ width: 90 }}
            >
              Réinitialiser
            </Button>
          </Space>
        </div>
      ),
      filterIcon: filtered => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
    },
    {
      title: 'Montant HT',
      dataIndex: 'MNT_HT',
      key: 'MNT_HT',
      sorter: true,
      render: value => value?.toFixed(2) || '0.00'
    },
    {
      title: 'Montant TTC',
      dataIndex: 'MNT_TTC',
      key: 'MNT_TTC',
      sorter: true,
      render: value => value?.toFixed(2) || '0.00'
    },
    {
      title: 'Ref. BR',
      dataIndex: 'REF_BR_A',
      key: 'REF_BR_A',
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Commence par"
            value={selectedKeys[0]}
            onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => {
              confirm();
              setRefBrSearch(selectedKeys[0]);
              setPagination(prev => ({ ...prev, current: 1 }));
            }}
            style={{ marginBottom: 8, display: 'block' }}
            ref={node => node && setTimeout(() => node.focus(), 0)}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => {
                confirm();
                setRefBrSearch(selectedKeys[0]);
                setPagination(prev => ({ ...prev, current: 1 }));
              }}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Rechercher
            </Button>
            <Button
              onClick={() => {
                clearFilters();
                setRefBrSearch('');
                setPagination(prev => ({ ...prev, current: 1 }));
              }}
              size="small"
              style={{ width: 90 }}
            >
              Réinitialiser
            </Button>
          </Space>
        </div>
      ),
      filterIcon: filtered => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
    },
    {
      title: 'Remarque',
      dataIndex: 'REMARQUE',
      key: 'REMARQUE',
      ellipsis: true,
    },
  ];

  const handleTableChange = (pagination, filters, sorter) => {
    setPagination(pagination);
    setSorter(sorter);
  };

  const getRowStyle = (record) => {
    if (record.VALIDER) {
      return {
        backgroundColor: '#f6ffed', // Light green for validated
        cursor: 'pointer'
      };
    }
    return { cursor: 'pointer' };
  };

  return (
    <div>
      <Card style={{ marginTop: 20, backgroundColor: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#333', marginBottom: '16px' }}>
          Liste des Factures d'Achat
        </h2>

        <Form
          form={form}
          layout="inline"
          style={{ marginBottom: 16 }}
        >
          <Form.Item name="dateRange" label="Période">
            <RangePicker
              onChange={handleDateChange}
              value={dateRange}
              format="DD/MM/YYYY"
              placeholder={['Date début', 'Date fin']}
            />
          </Form.Item>
          <Form.Item>
            <Button
              icon={<FilterOutlined />}
              onClick={clearAllFilters}
            >
              Réinitialiser filtres
            </Button>
          </Form.Item>
        </Form>

        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab="Enregistrés" key="notValidated" />
          <TabPane tab="Validés" key="validated" />
        </Tabs>

        <Table
          columns={columns}
          dataSource={items}
          rowKey="id"
          loading={loading}
          pagination={{ ...pagination, total }}
          onChange={handleTableChange}
          onRow={(record) => ({
            onClick: () => onSelect && onSelect(record.id),
            onMouseEnter: () => setHoveredRow(record.REF_FAC_A),
            onMouseLeave: () => setHoveredRow(null),
            style: getRowStyle(record)
          })}
          size="small"
        />
      </Card>
    </div>
  );
};

export default FactureAchatList;
